import { readWidgetConfig, WIDGET_HOST_SELECTOR } from './readConfig';
import { getRegisteredWidgetApp, WIDGET_APP_READY_EVENT } from './appRegistry';
import {
  buildFallbackLinks,
  deriveAppOrigin,
  removeLightFallback,
  removeShadowFallback,
  removeSkeleton,
  renderLightFallback,
  renderShadowFallback,
  renderSkeleton,
  supportsWidgetRuntime,
} from './loaderShell';

export const MOUNTED_ATTR = 'data-meo-mounted';
export const INSTANCE_ATTR = 'data-meo-instance-id';

/** Show the link-out fallback if the app has not rendered by then. */
export const BOOT_TIMEOUT_MS = 10000;
/** Max wait for the first-paint prefetch once the app bundle is ready. */
export const PREFETCH_WAIT_MS = 3000;

function resolveAppUrl(appEntry, scriptSrc) {
  if (!appEntry || !scriptSrc) {
    return null;
  }
  try {
    return new URL(appEntry, scriptSrc).href;
  } catch {
    return null;
  }
}

export function buildPrefetchUrl(publicApiUrl, eventId) {
  return `${publicApiUrl.replace(/\/+$/, '')}/events/${encodeURIComponent(eventId)}/widget`;
}

function normalizePrefetch(data, eventId) {
  const event = data?.event;
  if (!event || typeof event !== 'object' || event.eventId !== eventId) {
    return null;
  }
  return {
    event,
    ticketTypes: Array.isArray(data.ticketTypes) ? data.ticketTypes : undefined,
  };
}

/**
 * Widget loader runtime (MER-702). Everything here runs from the small
 * classic-script widget.js: it paints a skeleton immediately, starts the
 * first-paint fetch in parallel, injects the hashed app module, and falls
 * back to a link to the hosted register page if the app never renders.
 */
export function createWidgetLoader({
  win = window,
  doc = document,
  scriptSrc = '',
  appEntry = '',
  publicApiUrl = '',
  version = 'dev',
  bootTimeoutMs = BOOT_TIMEOUT_MS,
  prefetchWaitMs = PREFETCH_WAIT_MS,
  isSupported = supportsWidgetRuntime,
} = {}) {
  /** @type {Map<Element, object>} */
  const instances = new Map();
  let instanceSeq = 0;
  let appPromise = null;
  const appUrl = resolveAppUrl(appEntry, scriptSrc);
  const appOrigin = deriveAppOrigin(scriptSrc);

  function loadApp() {
    if (appPromise) {
      return appPromise;
    }
    appPromise = new Promise((resolve, reject) => {
      if (!appUrl) {
        reject(new Error('[MeoWidget] App bundle URL unavailable'));
        return;
      }
      const registered = getRegisteredWidgetApp(win, appUrl);
      if (registered) {
        resolve(registered);
        return;
      }

      const script = doc.createElement('script');
      const fail = (message) => {
        win.removeEventListener(WIDGET_APP_READY_EVENT, onReady);
        script.remove();
        appPromise = null;
        reject(new Error(message));
      };
      function onReady() {
        const app = getRegisteredWidgetApp(win, appUrl);
        if (app) {
          win.removeEventListener(WIDGET_APP_READY_EVENT, onReady);
          resolve(app);
        }
      }
      win.addEventListener(WIDGET_APP_READY_EVENT, onReady);
      script.type = 'module';
      script.crossOrigin = 'anonymous';
      script.src = appUrl;
      script.addEventListener('error', () => fail('[MeoWidget] Failed to load app bundle'), { once: true });
      // Module parse/eval errors do not fire `error`; `load` without a
      // registration means the bundle could not run in this browser.
      script.addEventListener('load', () => {
        if (!getRegisteredWidgetApp(win, appUrl)) {
          fail('[MeoWidget] App bundle did not register');
        }
      }, { once: true });
      (doc.head || doc.documentElement).appendChild(script);
    });
    return appPromise;
  }

  function prefetch(eventId) {
    if (!publicApiUrl || typeof win.fetch !== 'function') {
      return Promise.resolve(null);
    }
    // No custom headers: keeps this a CORS "simple" request (no preflight).
    return win.fetch(buildPrefetchUrl(publicApiUrl, eventId), { credentials: 'omit' })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => normalizePrefetch(data, eventId))
      .catch(() => null);
  }

  function waitAtMost(promise, ms) {
    return new Promise((resolve) => {
      const timer = win.setTimeout(() => resolve(null), ms);
      promise.then((value) => {
        win.clearTimeout(timer);
        resolve(value);
      });
    });
  }

  function fallbackLinksFor(inst) {
    return buildFallbackLinks({
      container: inst.container,
      config: inst.config,
      appOrigin,
      search: win.location?.search || '',
    });
  }

  function markReady(inst) {
    inst.ready = true;
    win.clearTimeout(inst.timer);
    removeSkeleton(inst.shadowRoot);
    removeShadowFallback(inst.shadowRoot);
  }

  function showFallback(inst) {
    if (inst.cancelled || inst.ready) {
      return;
    }
    renderShadowFallback(inst.shadowRoot, fallbackLinksFor(inst));
  }

  function mount(container, overrides = {}) {
    if (!container || container.nodeType !== 1) {
      return null;
    }
    const existing = instances.get(container);
    if (existing) {
      return { instanceId: existing.instanceId };
    }

    const config = { ...readWidgetConfig(container), ...overrides };
    if (!config.eventId) {
      // eslint-disable-next-line no-console
      console.warn('[MeoWidget] Missing data-event-id on container; skip mount.');
      return null;
    }

    const inst = {
      instanceId: `meo-${++instanceSeq}`,
      container,
      config,
      shadowRoot: null,
      handle: null,
      timer: null,
      ready: false,
      cancelled: false,
    };
    instances.set(container, inst);
    container.setAttribute(MOUNTED_ATTR, 'true');
    container.setAttribute(INSTANCE_ATTR, inst.instanceId);

    if (!isSupported(win)) {
      renderLightFallback(container, fallbackLinksFor(inst));
      return { instanceId: inst.instanceId };
    }

    inst.shadowRoot = container.shadowRoot || container.attachShadow({ mode: 'open' });
    renderSkeleton(inst.shadowRoot);

    const data = prefetch(config.eventId);
    inst.timer = win.setTimeout(() => showFallback(inst), bootTimeoutMs);

    loadApp()
      .then((app) => waitAtMost(data, prefetchWaitMs).then((prefetched) => {
        if (inst.cancelled) {
          return null;
        }
        return app.render({
          shadowRoot: inst.shadowRoot,
          config,
          prefetched,
          fallbackLinks: fallbackLinksFor(inst),
          onReady: () => markReady(inst),
        });
      }))
      .then((handle) => {
        if (!handle) {
          return;
        }
        if (inst.cancelled) {
          handle.unmount();
          return;
        }
        inst.handle = handle;
      })
      .catch(() => {
        win.clearTimeout(inst.timer);
        showFallback(inst);
      });

    return { instanceId: inst.instanceId };
  }

  function unmount(container) {
    const inst = instances.get(container);
    if (inst) {
      inst.cancelled = true;
      win.clearTimeout(inst.timer);
      inst.handle?.unmount();
      instances.delete(container);
    }
    if (container?.shadowRoot) {
      container.shadowRoot.innerHTML = '';
    }
    removeLightFallback(container);
    container?.removeAttribute?.(MOUNTED_ATTR);
    container?.removeAttribute?.(INSTANCE_ATTR);
  }

  function mountAll(root = doc) {
    if (!root?.querySelectorAll) {
      return 0;
    }
    let count = 0;
    root.querySelectorAll(WIDGET_HOST_SELECTOR).forEach((el) => {
      if (instances.has(el)) {
        return;
      }
      if (mount(el)) {
        count += 1;
      }
    });
    return count;
  }

  function observeNewHosts() {
    if (typeof win.MutationObserver === 'undefined' || !doc.documentElement) {
      return;
    }
    const observer = new win.MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) {
            continue;
          }
          if (node.matches?.(WIDGET_HOST_SELECTOR)) {
            mount(node);
          }
          node.querySelectorAll?.(WIDGET_HOST_SELECTOR).forEach((el) => mount(el));
        }
      }
    });
    observer.observe(doc.documentElement, { childList: true, subtree: true });
  }

  /**
   * Mount right away: the snippet's script is async, so hosts above it are
   * usually already parsed. The observer catches hosts parsed (or injected)
   * later, and DOMContentLoaded is a final sweep — never a prerequisite.
   */
  function boot() {
    mountAll(doc);
    observeNewHosts();
    if (doc.readyState === 'loading') {
      doc.addEventListener('DOMContentLoaded', () => mountAll(doc), { once: true });
    }
  }

  return {
    api: {
      mount,
      unmount,
      mountAll: (root) => mountAll(root),
      version,
    },
    boot,
    loadApp,
  };
}
